import { NO_TARGET, type InteractTarget } from "../interact-target.ts";

/**
 * The thing E acts on, as world-attached UI reads it (P1.9-T17): the scene resolves it from its target lookup
 * (`interact-target.ts`), which E shares, so a surface such as the interaction prompt names exactly what E would act
 * on, and the scene names no surface.
 */

/**
 * Where the target comes from.
 */
export interface TargetSource {
  /**
   * The thing E would act on now, for the predicted local character: the same object every call, rescanned only
   * when the predicted feet box's cells or the things in view change.
   *
   * @returns The target, `found` false when nothing is in reach or the scene is not in the world.
   */
  current(): InteractTarget;
  /**
   * Whether the key is withheld from the thing named: true while the local character's effects forbid acting, when
   * the map server refuses its interact (ADR-079's amendment of 2026-10-07, point 5), so a surface draws the thing
   * and its verb without offering the key.
   *
   * @returns True while E would send nothing.
   */
  withheld(): boolean;
  /**
   * Why the thing named refused the verb it offers, the design's `refused` state (P1.8-T4): the line the page words for
   * the map server's answer, drawn under the prompt, or empty while nothing refused it.
   *
   * @param handle The thing's handle, the current target's.
   * @param verb The verb it offers now.
   * @returns The line, or "".
   */
  refusal(handle: number, verb: string): string;
  /**
   * How many times the lookup has scanned the view, for the report.
   *
   * @returns The count.
   */
  scanCount(): number;
}

/**
 * The source of a scene with no world to act on, as the `/ui` gallery's pages.
 */
export const NO_TARGET_SOURCE: TargetSource = Object.freeze({
  /**
   * Finds nothing.
   *
   * @returns Nothing in reach.
   */
  current: (): InteractTarget => NO_TARGET,
  /**
   * Withholds no key.
   *
   * @returns False.
   */
  withheld: (): boolean => false,
  /**
   * Names no refusal.
   *
   * @returns "".
   */
  refusal: (): string => "",
  /**
   * Has scanned nothing.
   *
   * @returns 0.
   */
  scanCount: (): number => 0,
});
